/**
 * Context that indicates whether the app is running inside the React Native
 * app's WebView (e.g. the Showpass mobile app) vs in a regular browser.
 *
 * Use this when you need to change behaviour for the mobile webviewConfig: different
 * UI, avoid certain features, or communicate with the native shell.
 *
 * The app must be wrapped in MobileWebviewProvider (e.g. in _app.tsx).
 * useMobileWebviewContext() throws if used outside the provider so that
 * missing or mis-placed provider is caught immediately.
 */
import axios from "axios";
import Cookies from "js-cookie";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import { mobileWebviewActiveCookie } from "@shared/constants/react-native";
import {
  postMessageToReactNativeWebView,
  type WebviewAction,
  type WebviewMessageMap,
} from "@shared/utils/webview";

type WebviewAdapterConfig = {
  [K in WebviewAction]: WebviewMessageMap[K] extends undefined
    ? {
        action: K;
        preAction?: () => void;
        postAction?: () => void;
        enabled?: boolean;
      }
    : {
        action: K;
        payload: WebviewMessageMap[K];
        preAction?: () => void;
        postAction?: () => void;
        enabled?: boolean;
      };
}[WebviewAction];

type MobileWebviewContextValue = {
  /**
   * True when the page is running inside the React Native app's WebView (e.g.
   * the Showpass mobile app). False when opened in Safari, Chrome, or any other
   * standalone browser.
   */
  isMobileWebviewActive: boolean;

  /**
   * Selects between a web and webview value based on the current platform.
   * Both values are optional — returns `undefined` if the active platform has no value.
   *
   * @example
   * flexDirection: webviewValueAdapter({ web: "row", webview: "column" })
   * style={webviewValueAdapter({ web: {}, webview: { display: "none" } })}
   * // Webview-only value
   * const msg = webviewValueAdapter({ webview: "webview-toast" });
   */
  webviewValueAdapter: <T>(options: { web?: T; webview?: T }) => T | undefined;

  /**
   * Executes a platform-specific action based on the current environment.
   *
   * - `web`: optional callback run when NOT in the webview.
   * - `webview`: optional declarative config that posts a message to the React
   *   Native shell via `postMessageToReactNativeWebView`. Both branches are
   *   optional — omitting one is a no-op for that platform.
   *
   * @example
   * // PDF download
   * webviewPostMessageAdapter({
   *   web: () => { document.location.href = url; },
   *   webview: { action: WebviewActions.PDF_DOWNLOAD, payload: { url } },
   * });
   *
   * // Webview-only action with guard
   * webviewPostMessageAdapter({
   *   webview: {
   *     action: WebviewActions.OPEN_APPLE_WALLET,
   *     payload: { url: apple },
   *     preAction: () => event.preventDefault(),
   *     enabled: Boolean(apple),
   *   },
   * });
   */
  webviewPostMessageAdapter: (options: {
    web?: () => void;
    webview?: WebviewAdapterConfig;
  }) => void;
};

const MobileWebviewContext = createContext<MobileWebviewContextValue | null>(
  null
);

/** Cookie name for the webview JWT auth token set by the React Native shell. */
export const userWebviewAuthToken = "showpass-webview-auth-token";

/**
 * Clears all webview auth state: removes the axios Authorization header and
 * the webview JWT cookie. Call this when logging out of a webview session.
 */
export function clearWebviewAuth(): void {
  delete axios.defaults.headers.common.Authorization;
  Cookies.remove(userWebviewAuthToken);
}

/** Parses the full WebviewConfig from a raw cookie header string (for use in SSR / getInitialProps). */
export function parseWebviewConfig(cookie: string): WebviewConfig {
  return {
    isMobileWebviewActive: cookie.includes(`${mobileWebviewActiveCookie}=true`),
    authToken:
      cookie
        .split("; ")
        .find((c) => c.startsWith(`${userWebviewAuthToken}=`))
        ?.split("=")[1] || undefined,
  };
}

/**
 * SSR-safe config passed from _app.tsx getInitialProps.
 * Shared between AppPropsWithLayout and MobileWebviewProvider.
 */
export type WebviewConfig = {
  isMobileWebviewActive: boolean;
  authToken?: string;
};

/**
 * Provides webviewConfig detection to the tree. Mount once at app root (e.g. in
 * _app.tsx). Pass isMobileWebviewActive based on your detection (e.g. URL param,
 * user agent, or cookie set by the native shell).
 *
 * When authToken is provided (webviewConfig JWT), sets the axios Authorization header
 * synchronously before any child renders so the first API call is authenticated.
 */
export function MobileWebviewProvider({
  children,
  webviewConfig,
}: {
  children: ReactNode;
  webviewConfig?: WebviewConfig;
}) {
  // Starts from SSR-detected cookie value. Can be updated asynchronously
  // by the backward compat observer below for old mobile app versions.
  const [isMobileWebviewActive, setMobileWebviewActive] = useState(
    Boolean(webviewConfig?.isMobileWebviewActive)
  );

  // Set axios Authorization header synchronously before any child renders.
  // webviewConfig is a fixed SSR value so this is idempotent across re-renders.
  if (webviewConfig?.authToken && typeof window !== "undefined") {
    axios.defaults.headers.common.Authorization = `JWT ${webviewConfig.authToken}`;
  }

  // Backward compat for old mobile app versions that inject a DOM attribute
  // ("is-react-native-app") asynchronously instead of the cookie.
  // A MutationObserver is required because the attribute is set after page load.
  // WEBVIEW TODO: Remove once the mobile update has fully rolled out.
  useEffect(() => {
    const IS_REACT_NATIVE_APP = "is-react-native-app";

    let observer: MutationObserver;

    const checkAndActivate = () => {
      if (
        document.body.hasAttribute(IS_REACT_NATIVE_APP) ||
        window.location.href?.includes("/mobile/")
      ) {
        setMobileWebviewActive(true);
        observer?.disconnect();
      }
    };

    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === IS_REACT_NATIVE_APP
        ) {
          checkAndActivate();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkAndActivate();

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (axios.defaults.headers?.common) {
        delete axios.defaults.headers.common.Authorization;
      }
    };
  }, []);

  const webviewValueAdapter = useCallback(
    <T,>(options: { web?: T; webview?: T }): T | undefined => {
      return isMobileWebviewActive ? options.webview : options.web;
    },
    [isMobileWebviewActive]
  );

  const webviewPostMessageAdapter = useCallback(
    (options: { web?: () => void; webview?: WebviewAdapterConfig }): void => {
      if (isMobileWebviewActive) {
        if (!options.webview) return;
        const {
          preAction,
          postAction,
          enabled = true,
          ...message
        } = options.webview;
        if (!enabled) return;
        preAction?.();
        postMessageToReactNativeWebView(
          message as Parameters<typeof postMessageToReactNativeWebView>[0]
        );
        postAction?.();
      } else {
        options.web?.();
      }
    },
    [isMobileWebviewActive]
  );

  return (
    <MobileWebviewContext.Provider
      value={{
        isMobileWebviewActive,
        webviewValueAdapter,
        webviewPostMessageAdapter,
      }}
    >
      {children}
    </MobileWebviewContext.Provider>
  );
}

export function useMobileWebviewContext(): MobileWebviewContextValue {
  const context = useContext(MobileWebviewContext);

  if (context === null) {
    throw new Error(
      "useMobileWebviewContext must be used within a MobileWebviewProvider"
    );
  }

  return context;
}
